/**
 * Structures: maps that are a *kind of diagram* rather than a tree.
 *
 * A mind map, an org chart and a fishbone differ in how they draw one hierarchy. These differ in
 * what the hierarchy *means*, and so in the picture it becomes:
 *
 * - **Timeline** — the root is the title; each branch is an event on one axis, alternately above
 *   and below it, with its details listed under it.
 * - **Matrix** — each branch is a cell of a two-column grid (four branches: a 2×2), its items
 *   listed inside it.
 * - **Board** — each branch is a column of cards, side by side, like a kanban board.
 * - **Road** — each branch is a milestone on a winding road that turns back every few stops.
 * - **Layers** — each branch is a layer, and every item of a layer is linked to every item of the
 *   next: a pipeline, a funnel of sources into outcomes, a small network.
 *
 * The metro map is the sixth structure and has a file of its own (`metro.ts`).
 *
 * This file places the boxes. The picture around them — the axis, the cells, the lanes, the road,
 * the links between layers — is drawn by the scene from where the boxes *are*, not from where this
 * put them, so a box somebody dragged still sits on its road. Like every layout here: the root
 * does not move, a collapsed branch takes no room, a pinned node keeps its place, and a second run
 * over an arranged map reports nothing.
 */
import { childrenOf } from '@orbit-mind/domain';
import { LIST_INDENT } from './list';
import { onlyMoves } from './moves';
/** Between two rows of a column. */
const ROW_GAP = 10;
/** Timeline: from the axis to the nearest box of an event, and the least distance between events. */
export const TIMELINE_STEM = 34;
const TIMELINE_MIN_STEP = 96;
const TIMELINE_GAP = 24;
/** Matrix and board: the padding inside a cell or lane, and the gap between two of them. */
export const CELL_PAD = 16;
export const CELL_GAP = 18;
export const MIN_CELL = { width: 220, height: 150 };
export const LANE_MIN_WIDTH = 176;
/** Road: from a milestone's column to the road under it, stops per row, and room between rows. */
export const ROAD_RISE = 26;
const ROAD_PER_ROW = 4;
const ROAD_ROW_CLEARANCE = 62;
const ROAD_MIN_SLOT = 150;
/** Layers: between two layers, where the links run. */
const LAYER_GAP = 112;
function contextFor(input) {
    const { document } = input;
    const root = document.nodes[document.map.rootNodeId];
    if (root === undefined)
        return null;
    const placements = {};
    return {
        root,
        size: (node) => input.extentOf(node.id),
        children: (node) => node.collapsed ? [] : childrenOf(document, node.id).filter((child) => !child.hidden),
        place: (node, x, y) => {
            if (node.pinned === true && input.preserveManualPositions)
                return;
            placements[node.id] = { x, y };
        },
        pinnedAt: (node) => node.pinned === true && input.preserveManualPositions ? { x: node.x, y: node.y } : null,
        placements,
    };
}
/** A head and everything under it, as an outline read top to bottom. */
function rowsUnder(context, head) {
    const rows = [];
    const walk = (node, depth) => {
        for (const child of context.children(node)) {
            rows.push({ node: child, depth });
            walk(child, depth + 1);
        }
    };
    walk(head, 1);
    return rows;
}
function indentOf(depth, indent) {
    return LIST_INDENT * (indent === 'details' ? depth : depth - 1);
}
function columnOf(context, head, indent) {
    const rows = rowsUnder(context, head);
    const headSize = context.size(head);
    let width = headSize.width;
    let height = headSize.height;
    for (const row of rows) {
        const size = context.size(row.node);
        width = Math.max(width, indentOf(row.depth, indent) + size.width);
        height += ROW_GAP + size.height;
    }
    return { head, rows, width, height };
}
/**
 * Stacks a column's rows from `top` down.
 *
 * `centre` centres the head's own children in the column (the layers), rather than lining them up
 * on its left edge; whatever hangs from an item still hangs, indented, from the item.
 */
function placeRows(context, column, x, top, indent, centre = false) {
    let cursor = top;
    let itemLeft = x;
    for (const row of column.rows) {
        const size = context.size(row.node);
        let left;
        if (centre) {
            if (row.depth === 1)
                itemLeft = x + (column.width - size.width) / 2;
            left = itemLeft + LIST_INDENT * (row.depth - 1);
        }
        else {
            left = x + indentOf(row.depth, indent);
        }
        context.place(row.node, left, cursor);
        cursor += size.height + ROW_GAP;
    }
}
/**
 * Places a column with its head's top-left at `(x, y)`, the rows stacked under it — or under the
 * head where somebody dragged it, so an event, a cell's heading or a milestone takes its details
 * along.
 */
function placeColumn(context, column, x, y, indent) {
    const at = context.pinnedAt(column.head) ?? { x, y };
    context.place(column.head, at.x, at.y);
    placeRows(context, column, at.x, at.y + context.size(column.head).height + ROW_GAP, indent);
}
function result(input, context) {
    return { positions: onlyMoves(input.document, context.placements) };
}
export function packTimeline(input) {
    const context = contextFor(input);
    if (context === null)
        return { positions: {} };
    const { root } = context;
    const rootSize = context.size(root);
    const axisY = root.y + rootSize.height / 2;
    const start = root.x + rootSize.width + 40;
    const cursor = { above: start, below: start };
    let lastCentre = Number.NEGATIVE_INFINITY;
    context.children(root).forEach((event, index) => {
        const column = columnOf(context, event, 'details');
        const headWidth = context.size(event).width;
        const side = index % 2 === 0 ? 'above' : 'below';
        const x = Math.max(cursor[side], lastCentre + TIMELINE_MIN_STEP - headWidth / 2);
        const y = side === 'above' ? axisY - TIMELINE_STEM - column.height : axisY + TIMELINE_STEM;
        placeColumn(context, column, x, y, 'details');
        cursor[side] = x + column.width + TIMELINE_GAP;
        lastCentre = x + headWidth / 2;
    });
    return result(input, context);
}
export function packMatrix(input) {
    const context = contextFor(input);
    if (context === null)
        return { positions: {} };
    const { root } = context;
    const rootSize = context.size(root);
    const cells = context.children(root).map((head) => columnOf(context, head, 'items'));
    if (cells.length === 0)
        return result(input, context);
    const columns = cells.length === 1 ? 1 : 2;
    const cellWidth = Math.max(MIN_CELL.width, ...cells.map((cell) => cell.width + CELL_PAD * 2));
    const cellHeight = Math.max(MIN_CELL.height, ...cells.map((cell) => cell.height + CELL_PAD * 2));
    const gridWidth = columns * cellWidth + (columns - 1) * CELL_GAP;
    const left = root.x + rootSize.width / 2 - gridWidth / 2;
    const top = root.y + rootSize.height + 36;
    cells.forEach((cell, index) => {
        const column = index % columns;
        const row = Math.floor(index / columns);
        placeColumn(context, cell, left + column * (cellWidth + CELL_GAP) + CELL_PAD, top + row * (cellHeight + CELL_GAP) + CELL_PAD, 'items');
    });
    return result(input, context);
}
export function packBoard(input) {
    const context = contextFor(input);
    if (context === null)
        return { positions: {} };
    const { root } = context;
    const rootSize = context.size(root);
    const lanes = context.children(root).map((head) => columnOf(context, head, 'items'));
    const laneWidth = Math.max(LANE_MIN_WIDTH, ...lanes.map((lane) => lane.width + CELL_PAD * 2));
    const top = root.y + rootSize.height + 32;
    lanes.forEach((lane, index) => {
        placeColumn(context, lane, root.x + index * (laneWidth + CELL_GAP) + CELL_PAD, top + CELL_PAD, 'items');
    });
    return result(input, context);
}
export function packRoad(input) {
    const context = contextFor(input);
    if (context === null)
        return { positions: {} };
    const { root } = context;
    const rootSize = context.size(root);
    const stops = context.children(root).map((head) => columnOf(context, head, 'details'));
    if (stops.length === 0)
        return result(input, context);
    const slot = Math.max(ROAD_MIN_SLOT, ...stops.map((stop) => stop.width + 40));
    const tallest = Math.max(...stops.map((stop) => stop.height));
    const pitch = tallest + ROAD_RISE + ROAD_ROW_CLEARANCE;
    const firstRoad = root.y + rootSize.height / 2;
    const start = root.x + rootSize.width + 56;
    stops.forEach((stop, index) => {
        const row = Math.floor(index / ROAD_PER_ROW);
        const within = index % ROAD_PER_ROW;
        // Every other row runs back the way it came, so the road winds rather than wrapping.
        const position = row % 2 === 0 ? within : ROAD_PER_ROW - 1 - within;
        const centre = start + position * slot + slot / 2;
        const road = firstRoad + row * pitch;
        const headWidth = context.size(stop.head).width;
        placeColumn(context, stop, centre - headWidth / 2, road - ROAD_RISE - stop.height, 'details');
    });
    return result(input, context);
}
export function packLayers(input) {
    const context = contextFor(input);
    if (context === null)
        return { positions: {} };
    const { root } = context;
    const rootSize = context.size(root);
    const layers = context.children(root);
    if (layers.length === 0)
        return result(input, context);
    const columns = layers.map((layer) => {
        const column = columnOf(context, layer, 'items');
        const headHeight = context.size(layer).height;
        return { column, body: column.height - headHeight };
    });
    const total = columns.reduce((sum, entry) => sum + entry.column.width, 0) + (columns.length - 1) * LAYER_GAP;
    const top = root.y + rootSize.height + 36;
    const headBand = Math.max(...layers.map((layer) => context.size(layer).height));
    const tallest = Math.max(...columns.map((entry) => entry.body));
    let x = root.x + rootSize.width / 2 - total / 2;
    for (const { column, body } of columns) {
        const headSize = context.size(column.head);
        context.place(column.head, x + (column.width - headSize.width) / 2, top);
        // The layer's items, centred under its heading and on the tallest layer, so the links between
        // two layers fan out evenly.
        placeRows(context, column, x, top + headBand + 24 + (tallest - body) / 2, 'items', true);
        x += column.width + LAYER_GAP;
    }
    return result(input, context);
}
function structure(id, name, run) {
    return { id, name, run };
}
export const timelineLayout = structure('timeline', 'Timeline', packTimeline);
export const matrixLayout = structure('matrix', '2×2 matrix', packMatrix);
export const boardLayout = structure('board', 'Board', packBoard);
export const roadLayout = structure('road', 'Road', packRoad);
export const layersLayout = structure('layers', 'Layers', packLayers);
//# sourceMappingURL=structures.js.map